import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Divider, Drawer, Menu, Modal } from "antd";
import { DownOutlined } from "@ant-design/icons";
import newStyled from "@emotion/styled";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../styles/Navbar.css";
import dashIcon from "../../assets/space_dashboard.png";
import reportIcon from "../../assets/insert_chart.png";
import actionIcon from "../../assets/bottom_right_click.png";
import upcomingIcon from "../../assets/chronic.png";
import GTranslateWidget from "../../utils/widgets/GTranslateWidget";
import ProfileModal from "../modals/ProfileModal";
import useClientAdmin from "../../utils/hooks/IsClientAdmin";
import { handleError, handleSuccess } from "../../utils/Service/utils";
import { useTenantContext } from "../../utils/Context/TenantsContext";
import { useFetchFunction } from "../../utils/hooks/UseFetchUrl";
import { base_url } from "../../utils/apiConfig";

// Below this width the nav collapses into: logo + hamburger + drawer
const COMPACT_QUERY = "(max-width: 1289px)";

const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(
    () => window.matchMedia(query).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(query);

    const onChange = (e) => setMatches(e.matches);
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [query]);
  return matches;
};

const Navbar = () => {
  const tenant = useTenantContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isCompact = useMediaQuery(COMPACT_QUERY);
  const { isClientAdmin, clientId } = useClientAdmin();
  const { data: configData, getData: getConfigData } = useFetchFunction();
  const { data: userData, getData: getUserData } = useFetchFunction();
  // Dashboard Visibility (set by FloorWalk per client); the AI Insights tab shows unless switched off
  const { data: widgetAccess, getData: getWidgetAccess } = useFetchFunction();
  const {
    loading: notificationLoading,
    data: notificationData,
    getData: getNotificationData,
  } = useFetchFunction();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [emailNotification, setEmailNotification] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  // =============================== Effects =========================================
  useEffect(() => {
    getConfigData("/config");
    getUserData("/user");
    getWidgetAccess("dashboard_widget_access");
    getNotificationData("/email_notification");
  }, []);

  useEffect(() => {
    if (typeof notificationData?.receive_email_notification === "boolean") {
      setEmailNotification(notificationData.receive_email_notification);
    }
  }, [notificationData]);

  useEffect(() => {
    if (!isCompact) setDrawerOpen(false);
  }, [isCompact]);

  // =============================== Nav config ======================================
  const reportItems = [
    { key: "auditBrowser", label: "Audit Browser" },
    { key: "reportbrowser", label: "Section Browser" },
    { key: "storebrowser", label: "Store Browser" },
    ...(isClientAdmin
      ? [{ key: "store-performance", label: "Store Performance" }]
      : []),
    { key: "question-performance", label: "Question Performance" },
    ...(isClientAdmin && clientId !== 378
      ? [{ key: "net-promoter-score", label: "Net Promoter Score" }]
      : []),
  ];
  const reportKeys = reportItems.map((item) => item.key);

  const navItems = [
    {
      key: "dashboard",
      label: "Dashboard",
      icon: <img src={dashIcon} alt="" />,
    },
    {
      key: "reports",
      label: "Reports",
      icon: <img src={reportIcon} alt="" />,
      popupClassName: "nav-popup",
      children: reportItems,
    },
    // Action Track is hidden for ci_ tokens
    !(localStorage.getItem("authToken") || "").startsWith("ci_") && {
      key: "actiontrack-report",
      label: "Action Track",
      icon: <img src={actionIcon} alt="" />,
    },
    isClientAdmin &&
      clientId !== 166 && {
        key: "up-coming-audits",
        label: "Upcoming Audits",
        icon: <img src={upcomingIcon} alt="" />,
      },
    isClientAdmin &&
      widgetAccess?.ai_insights !== false && {
        key: "ai-overall-report-analysis",
        label: "AI Insights",
        icon: <i className="bi bi-stars" />,
        className: "nav-ai",
      },
  ].filter(Boolean);

  // Desktop only: show the same chevron the old "Reports" pill had
  // (antd hides its own arrow on top-level horizontal submenus)
  const desktopItems = navItems.map((item) =>
    item.children
      ? {
          ...item,
          label: (
            <>
              Reports
              <DownOutlined />
            </>
          ),
        }
      : item,
  );

  // Active item comes straight from the URL — no extra state needed
  const selectedKey = location.pathname.split("/")[1] || "dashboard";

  // =============================== Handlers ========================================
  const getClientLogo = (client) => {
    const logoAttachment = client?.attachments?.find(
      (att) => att.attachment_category === "CLIENT_LOGO",
    );
    return (
      logoAttachment?.extra?.thumbnail_url || client?.logo_url || tenant?.logo7x
    );
  };

  const handleNavClick = ({ key }) => {
    // kept in localStorage in case other components still read these
    localStorage.setItem("activeTab", key);
    localStorage.setItem(
      "activeMainTab",
      reportKeys.includes(key) ? "report" : key,
    );
    navigate(`/${key}`);
    setDrawerOpen(false);
  };

  const openDrawer = () => {
    setOpenKeys(reportKeys.includes(selectedKey) ? ["reports"] : []);
    setDrawerOpen(true);
  };

  const openNotificationModal = () => {
    setDrawerOpen(false);
    setIsModalVisible(true);
  };

  const handleSave = async () => {
    const result = await getNotificationData("/email_notification", {
      receive_email_notification: emailNotification,
    });

    if (!result.success) {
      if (!result.aborted) {
        handleError("Failed to save email notification preference");
      }
      return;
    }

    handleSuccess("Successfully saved email notification preference");
    setIsModalVisible(false);

    const refreshResult = await getNotificationData("/email_notification");
    if (
      refreshResult.success &&
      typeof refreshResult.data?.receive_email_notification === "boolean"
    ) {
      setEmailNotification(refreshResult.data.receive_email_notification);
    }
  };

  const handleLogOut = async () => {
    setLoggingOut(true);
    const fullUrl = `${base_url.protocol}://${base_url.hostname}:${base_url.port}${base_url.api_base_path}client/market_place/logout_api`;
    const token = localStorage.getItem("authToken");
    try {
      await axios.post(
        fullUrl,
        {},
        { headers: { Authorization: `Token ${token}` } },
      );
      [
        "authToken",
        "activeTab",
        "activeMainTab",
        "userDetails",
        "userEmail",
        "userEmailDetails",
        "reportId",
        "sections",
        "selectedAuditId",
        "percentage",
        "searchResults",
      ].forEach((key) => localStorage.removeItem(key));
      handleSuccess("Logout successful");
      navigate("/login", { replace: true });
      setTimeout(() => window.location.reload(), 100);
    } catch (error) {
      console.log("An error occurred:", error);
      handleError("Logout unsuccessful");
    } finally {
      setLoggingOut(false);
    }
  };

  // =============================== Render ==========================================
  const email = userData?.email || configData?.USER_EMAIL;

  const profile = (
    <ProfileModal
      client_name={userData?.full_name}
      clientData={userData?.client}
      email={email}
      onLogout={handleLogOut}
      loggingOut={loggingOut}
      onEmailNotification={openNotificationModal}
    />
  );

  return (
    <div className="no-print">
      <nav className="header navbar navbar-expand-lg">
        <div className="container-fluid d-flex">
          <div
            className="logo-wrapper"
            onClick={() => handleNavClick({ key: "dashboard" })}
          >
            <img
              src={getClientLogo(userData?.client)}
              alt="Brand Logo"
              className="logo"
            />
          </div>

          {isCompact ? (
            <div
              onClick={openDrawer}
              className="bar"
              role="button"
              aria-label="Open menu"
            >
              <i style={{ fontSize: "2rem" }} className="bi bi-list"></i>
            </div>
          ) : (
            <>
              <Menu
                mode="horizontal"
                className="nav-menu"
                items={desktopItems}
                selectedKeys={[selectedKey]}
                onClick={handleNavClick}
                triggerSubMenuAction="click"
              />
              <div className="collNav">
                <div className="collInner d-flex justify-content-end gap-3">
                  <GTranslateWidget />
                  {profile}
                </div>
              </div>
            </>
          )}
        </div>
      </nav>

      {/* forceRender keeps the translate widget mounted so it initialises on page load */}
      <Drawer
        title="Menu"
        placement="right"
        width={280}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        forceRender
        className="nav-drawer"
      >
        <Menu
          mode="inline"
          className="drawer-menu"
          items={navItems}
          selectedKeys={[selectedKey]}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
          onClick={handleNavClick}
        />

        <div className="drawer-tools">
          <div className="drawer-row">
            <GTranslateWidget />
            <span>Choose Language</span>
          </div>
          <div className="drawer-row">
            {profile}
            <span className="drawer-email" title={email}>
              {email}
            </span>
          </div>
        </div>
      </Drawer>

      <ModalStl
        title="Receive Email Notification"
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Divider />
        <p className="emailNoty">
          Do you want to recevie email notification on {configData?.USER_EMAIL}
        </p>
        <div className="checkBoxContainer">
          <label>
            <input
              type="checkbox"
              className="checkBoxModal"
              checked={emailNotification}
              onChange={(e) => setEmailNotification(e.target.checked)}
              disabled={notificationLoading}
            />
          </label>
          <Button
            className="buttonModal"
            onClick={handleSave}
            loading={notificationLoading}
            disabled={notificationLoading}
          >
            Save
          </Button>
        </div>
      </ModalStl>

      <ToastContainer />
    </div>
  );
};

const ModalStl = newStyled(Modal)`
:where(.css-dev-only-do-not-override-1kf000u).ant-modal .ant-modal-title {
    font-family: 'Roboto', sans-serif;
    font-weight: 500;
    color: #003C5D !important;
}
`;

export default Navbar;
